import React, { useEffect, useState } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { clearAuthData, decodeToken, getTokenRole, hasActiveAuthSession, normalizeRole } from "../../utils/authUtils";
import { useTenant } from "../../context/TenantContext";

export default function ProtectedRoute({ allowedRoles, requiredModule }: { allowedRoles?: string[]; requiredModule?: string }) {
  const [isValid, setIsValid] = useState<boolean | null>(null);
  const token = localStorage.getItem("token");
  const hasSession = hasActiveAuthSession();
  const storedRole = normalizeRole(localStorage.getItem("role"));
  const tokenRole = getTokenRole(token || "");
  const userRole = storedRole || tokenRole;
  const { hasModule, loading: tenantLoading } = useTenant();
  const location = useLocation();
  const subscriptionBlocked = localStorage.getItem("subscriptionBlocked") === "true";
  const blockedReason = localStorage.getItem("subscriptionBlockedReason");
  const allowBlockedAccess = userRole === "admin" && location.pathname === "/settings/subscription" && blockedReason !== "suspended";

  useEffect(() => {
    if (!token || !hasSession) {
      setIsValid(false);
      return;
    }

    try {
      // Decode token to check expiration
      const decoded = decodeToken(token);

      if (!decoded) {
        clearAuthData();
        setIsValid(false);
        return;
      }
      // Check if token is expired
      if (decoded.exp && decoded.exp * 1000 < Date.now()) {
        clearAuthData();
        setIsValid(false);
        return;
      }
      // Token is valid
      setIsValid(true);
    } catch (error) {
      clearAuthData();
      setIsValid(false);
    }
  }, [token, hasSession]);
  // Show loading state while checking token
  if (isValid === null) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 mb-4"></div>
          <p className="text-gray-600 font-medium">Verifying authentication...</p>
        </div>
      </div>
    );
  }

  if (!isValid || !token || !hasSession) {
    return <Navigate to="/signin" replace />;
  }

  const getFallbackPath = (role?: string | null) => {
    switch (normalizeRole(role)) {
      case "superadmin":
        return "/superadmin";
      case "admin":
        return "/admindashboard";
      case "headteacher":
        return "/headteacher";
      case "teacher":
        return "/teacherdashboard";
      case "student":
        return "/dashboard";
      default:
        return "/dashboard";
    }
  };
  // Check if user has required role
  if (allowedRoles && allowedRoles.length > 0 && userRole) {
    const normalizedAllowed = allowedRoles.map(role => normalizeRole(role));
    if (!normalizedAllowed.includes(userRole)) {
      return <Navigate to={getFallbackPath(userRole)} replace />;
    }
  }

  if (subscriptionBlocked && !allowBlockedAccess) {
    return <Navigate to="/subscription-blocked" replace />;
  }

  if (requiredModule && userRole !== "superadmin") {
    if (tenantLoading) {
      return (
        <div className="flex items-center justify-center min-h-screen bg-gray-50">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 mb-4"></div>
            <p className="text-gray-600 font-medium">Checking module access...</p>
          </div>
        </div>
      );
    }

    if (!hasModule(requiredModule)) {
      return (
        <div className="min-h-[60vh] flex items-center justify-center p-6">
          <div className="max-w-lg w-full bg-white border border-slate-200 rounded-3xl shadow-sm p-8 text-center">
            <h2 className="text-2xl font-bold text-slate-900 mb-3">Module Not Enabled</h2>
            <p className="text-slate-600">
              This module is not enabled for your school&apos;s deployment package. Contact your school administrator or superadmin if you need access.
            </p>
          </div>
        </div>
      );
    }
  }

  return <Outlet />;
}



